
import { useState } from 'react';

const faqs = [
  {
    q: 'How accurate is this salary tax calculator?',
    a: 'Our calculator uses the latest 2026 federal, state, and local tax brackets from official IRS and state revenue department sources. While individual circumstances like itemized deductions, tax credits, and special filing situations can affect your actual liability, our estimates are within 1-2% of actual for most standard W-2 employees.',
  },
  {
    q: 'What taxes are included in the take-home pay calculation?',
    a: 'We calculate federal income tax (using 2026 brackets), state income tax (all 50 states), local/city income tax where applicable, Social Security tax (6.2% up to $160,200), and Medicare tax (1.45% on all income). For UK calculations, we include Income Tax and National Insurance contributions.',
  },
  {
    q: 'Which states have no income tax?',
    a: 'Nine US states have no state income tax: Alaska, Florida, Nevada, New Hampshire, South Dakota, Tennessee, Texas, Washington, and Wyoming. Living in these states means you only pay federal taxes and FICA, resulting in significantly higher take-home pay. For example, a $100,000 earner in Texas keeps about $5,000-$13,000 more per year than the same earner in California or New York.',
  },
  {
    q: 'How does filing status affect my take-home pay?',
    a: 'Filing status significantly impacts your tax bracket and standard deduction. Single filers get a $13,850 standard deduction, while married filing jointly get $27,700. Head of household filers get $20,800. Married couples also benefit from wider tax brackets, meaning more income is taxed at lower rates.',
  },
  {
    q: 'Can I compare take-home pay between different cities?',
    a: 'Yes! Simply use our calculator for each city you want to compare. We cover 200+ US cities and UK locations. Each calculator automatically applies the correct federal, state, and local tax rates. You can also check our tax comparison table on this page for a quick side-by-side view of popular cities.',
  },
  {
    q: 'How often are the tax rates updated?',
    a: 'We update our tax rates annually when new federal and state tax brackets are released, typically in late October or November for the following tax year. Our current rates reflect the 2026 tax year. We also update throughout the year if states make mid-year changes to their tax codes.',
  },
  {
    q: 'Does the calculator account for pre-tax deductions like 401(k)?',
    a: 'Yes, our advanced settings allow you to enter pre-tax deductions including 401(k) contributions, HSA contributions, and other pre-tax benefits. These deductions reduce your taxable income, which can significantly lower your tax burden. For example, maxing out a 401(k) at $23,500 can save a Manhattan resident over $8,000 in combined taxes.',
  },
  {
    q: 'Is this calculator suitable for freelancers and self-employed workers?',
    a: 'Our calculator is primarily designed for W-2 employees. Self-employed individuals face additional self-employment tax (15.3% on net earnings) and different deduction rules. However, you can use our calculator as a baseline and add approximately 7.65% to your effective tax rate for a rough freelancer estimate. Check our blog for detailed freelancer tax guides by city.',
  },
];

const HomeFAQ = () => {
  // Start with no FAQ opened; using null is clearer than 0.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            <strong>Frequently Asked Questions About Salary Tax Calculations</strong>
          </h2>
          <p className="text-gray-500">Everything you need to know about calculating your take-home pay</p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleFAQ(i)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
                aria-expanded={openIndex === i}
                aria-controls={`faq-answer-${i}`}
              >
                <span className="text-base font-semibold text-gray-900 pr-4">{faq.q}</span>
                <i
                  className={`ri-arrow-${openIndex === i ? 'up' : 'down'}-s-line text-xl text-gray-400 flex-shrink-0`}
                  aria-hidden="true"
                ></i>
              </button>
              {openIndex === i && (
                <div
                  id={`faq-answer-${i}`}
                  className="px-6 pb-5 border-l-4 border-teal-500 bg-teal-50/50 animate-fadeIn"
                >
                  <p className="text-gray-700 leading-relaxed text-sm">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeFAQ;
